import { monthsToLabel } from '../mock';

function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function exportAmortizationCsv({
  schedule,
  loanAmount,
  interestRate,
  tenureMonths,
  currency = 'INR',
  advanced = {},
  summary,
  plainSummary,
  moratorium,
  extraEmi,
  prepayments,
  interestMethod,
}) {
  const fmt = (n) => Math.round(Number(n) || 0);
  const dRate = (advanced?.discountingRate == null || advanced?.discountingRate === '')
    ? Number(interestRate) || 0
    : Number(advanced.discountingRate);

  const monthlyExtraTotal = fmt((Number(extraEmi) || 0) * (summary?.monthsTaken || tenureMonths));
  const lumpSumTotal = (prepayments || []).reduce((s, p) => s + (Number(p?.amount) || 0), 0);
  const advOn = !!(advanced && (advanced.creditDaysEnabled || advanced.subventionEnabled));

  const details = [
    ['EMIWise Amortization Schedule'],
    ['Generated', new Date().toISOString()],
    [''],
    ['=== LOAN DETAILS ==='],
    ['Currency', currency],
    ['Interest method', interestMethod === 'flat' ? 'Flat rate' : 'Reducing balance'],
    ['Loan Amount', fmt(loanAmount)],
    ['Interest Rate (p.a. %)', Number(interestRate).toFixed(2)],
    ['Loan Tenure (months)', tenureMonths],
    ['Loan Tenure', monthsToLabel(tenureMonths)],
    ['Moratorium', moratorium?.enabled
      ? `${moratorium.months} months (${moratorium.type === 'full' ? 'interest capitalised' : 'interest-only paid'})`
      : 'None'],
    ['Credit Days', advanced?.creditDaysEnabled ? `${advanced.creditDays} days @ ${dRate.toFixed(2)}%` : 'None'],
    ['Interest Subvention', advanced?.subventionEnabled ? fmt(advanced.subventionAmount) : 'None'],
    [''],
    ['=== SUMMARY ==='],
    ['Actual EMI (no advance options)', fmt((plainSummary && plainSummary.emi) || summary?.emi)],
    ['EMI after advance options', fmt(summary?.emi)],
    ...(advOn && plainSummary
      ? [['EMI saving per month (advance)', fmt(Math.max(0, (plainSummary.emi || 0) - (summary?.emi || 0)))]]
      : []),
    ['Effective ROI (%)', Number(summary?.effectiveAnnualRate || interestRate).toFixed(3)],
    ['Monthly extra prepayment (per month)', fmt(extraEmi || 0)],
    ['Total monthly extra paid over tenure', monthlyExtraTotal],
    ['One-time lump-sum prepayments (count)', (prepayments || []).length],
    ['One-time lump-sum prepayments (total)', lumpSumTotal],
    ['Total prepayments (monthly + one-time)', monthlyExtraTotal + lumpSumTotal],
    ['Actual interest paid', fmt(summary?.totalInterest)],
    ['Actual amount to be paid', fmt(summary?.totalPayment)],
    ['Actual payoff duration', monthsToLabel(summary?.monthsTaken || tenureMonths)],
    [''],
  ];

  // One-time prepayments listed line by line for reference
  if (prepayments && prepayments.length) {
    details.push(['=== ONE-TIME PREPAYMENTS ===']);
    details.push(['Month', 'Amount']);
    prepayments.forEach((p) => details.push([p.month, fmt(p.amount)]));
    details.push(['']);
  }

  const headers = ['Month', 'Year', 'Phase', 'EMI Payment', 'Principal Paid', 'Interest Paid', 'Prepayment', 'Balance'];
  const rows = schedule.map((r) => [
    r.month,
    Math.ceil(r.month / 12),
    r.phase,
    fmt(r.emi),
    fmt(r.principalPaid),
    fmt(r.interestPaid),
    fmt(r.prepayment),
    fmt(r.balance),
  ]);

  const lines = [
    ...details.map((row) => row.map(csvEscape).join(',')),
    ['=== AMORTIZATION SCHEDULE ==='].map(csvEscape).join(','),
    headers.map(csvEscape).join(','),
    ...rows.map((row) => row.map(csvEscape).join(',')),
  ];

  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `EMI-Amortization-${Date.now()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}
